import React, { useContext, useEffect, useState, useRef } from "react";
import "./ChatSection.css";
import Darkmode from "../Darkmode/Darkmode";
import { LuSendHorizontal, LuCopy, LuSquare, LuMic, LuMicOff } from "react-icons/lu";
import { dataContext } from "../../context/UserContext";
import user from "../../assets/user1.png";
import ai from "../../assets/ai1.png";
import MarkdownRender from "../MarkdownRender/MarkdownRender";
import ResumePreview from "../ResumePreview/ResumePreview";

function ChatSection() {
    let { sent, input, setInput, showResult, loading, chatHistory, isStreaming, stopStreaming, suggestions, applySuggestion, regenerateLastAnswer, persona, selectPersona, promptTemplates, applyTemplate } = useContext(dataContext);
    
    // State to store user's name
    const [userName, setUserName] = useState("");

    useEffect(() => {
        const name = prompt("Hi, Greetings from Shashank Shekhar. Please enter your name:") || "User";
        setUserName(name.split(" ")[0]);
    }, []);

    // Key handling: Enter sends; Shift+Enter inserts newline into value manually (using hidden insertion)
    const handleKeyDown = (e) => {
        if(e.key === 'Enter'){
            if(e.shiftKey){
                e.preventDefault();
                const caretPos = inputRef.current ? inputRef.current.selectionStart : input.length;
                const before = input.slice(0, caretPos);
                const after = input.slice(caretPos);
                const newVal = before + '\n' + after;
                setInput(newVal);
                // restore caret position after state update
                requestAnimationFrame(()=>{
                    if(inputRef.current){ inputRef.current.selectionStart = inputRef.current.selectionEnd = caretPos + 1; }
                });
                return;
            }
            e.preventDefault();
            if(input.trim()) sent(input);
        }
    };

    // Handle send button click
    const handleSend = () => {
        if (input.trim()) {
            sent(input);
        }
    };

    // Platform detection (needed early for scroll thresholds)
    const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

    // Auto scroll to bottom on new messages
    const scrollRef = useRef(null);
    // Autoscroll: only if user near bottom (within 120px)
    const userScrolledUpRef = useRef(false);
    useEffect(() => {
        const el = scrollRef.current;
        if(!el) return;
        const baseThreshold = 120;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if(distanceFromBottom < baseThreshold || !userScrolledUpRef.current){
            el.scrollTop = el.scrollHeight;
        }
    }, [chatHistory]);
    // Track manual scroll to disable autoscroll until near bottom
    useEffect(()=>{
        const el = scrollRef.current;
        if(!el) return;
        function onScroll(){
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            // Android often reports larger early distances due to delayed layout; relax threshold there.
            const threshold = isAndroid ? 320 : 200;
            userScrolledUpRef.current = distanceFromBottom > threshold;
        }
        el.addEventListener('scroll', onScroll, { passive:true });
        return ()=> el.removeEventListener('scroll', onScroll);
    },[isAndroid]);
    function jumpToLatest(){
        const el = scrollRef.current; if(!el) return; el.scrollTop = el.scrollHeight; userScrolledUpRef.current=false; }

    // Mobile unified expand state for chips
    const [chipsExpanded, setChipsExpanded] = useState(false);
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width:600px)').matches;
    const personaList = ['default','concise','tutor','creative','reviewer'];
    const visiblePersona = isMobile && !chipsExpanded ? personaList.slice(0,3) : personaList;
    // On mobile hide all templates until expanded; on desktop show all.
    const visibleTemplates = isMobile && !chipsExpanded ? [] : promptTemplates;

    // iOS keyboard handling: scroll input into view and add temporary spacer
    const inputRef = useRef(null);
    const spacerRef = useRef(null);
    const suggestionRowRef = useRef(null);
    const bottomBarRef = useRef(null);
    // (moved isAndroid earlier)

    // Voice mode states
    const [voiceMode, setVoiceMode] = useState(false); // whether UI is in voice capture state
    const [isListening, setIsListening] = useState(false); // SpeechRecognition active
    const [recognitionError, setRecognitionError] = useState(null);
    const recognitionRef = useRef(null);
    const [voiceTranscript, setVoiceTranscript] = useState('');

    // Setup SpeechRecognition when voiceMode toggles on
    useEffect(()=>{
        if(!voiceMode){
            // Stop if leaving voice mode
            if(recognitionRef.current && isListening){
                try { recognitionRef.current.stop(); } catch(e) {}
            }
            setIsListening(false);
            setVoiceTranscript('');
            return;
        }
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if(!SpeechRecognition){
            setRecognitionError('Voice input not supported in this browser');
            return;
        }
        const rec = new SpeechRecognition();
        recognitionRef.current = rec;
        rec.lang = 'en-US';
        rec.interimResults = true;
        rec.continuous = false; // single prompt
        rec.maxAlternatives = 1;
        rec.onstart = ()=>{ setIsListening(true); setRecognitionError(null); };
        rec.onerror = (e)=>{ setRecognitionError(e.error || 'error'); setIsListening(false); };
        rec.onend = ()=>{ setIsListening(false); };
        let finalTranscript = '';
        rec.onresult = (e)=>{
            let interim = '';
            for(let i= e.resultIndex; i < e.results.length; i++){
                const res = e.results[i];
                if(res.isFinal){ finalTranscript += res[0].transcript; }
                else { interim += res[0].transcript; }
            }
            const combined = (finalTranscript || interim).trim();
            setVoiceTranscript(combined);
        };
        rec.onend = ()=>{
            setIsListening(false);
            const toSend = (voiceTranscript || finalTranscript).trim();
            if(toSend){ setInput(toSend); sent(toSend); }
            // Exit voice mode automatically after send
            setVoiceMode(false);
            setVoiceTranscript('');
        };
        try { rec.start(); } catch(e){ setRecognitionError('Unable to start mic'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voiceMode]);
    useEffect(() => {
        const ivv = typeof window !== 'undefined' ? window.visualViewport : null;
        if(!ivv) return;
        function isMobileViewport(){ return window.innerWidth <= 600; }
        function computeSpacer(){
            if(!spacerRef.current) return;
            if(!isMobileViewport()){
                spacerRef.current.style.height = '0px';
                return;
            }
            // Fixed 20px spacer for Android request
            if(isAndroid){
                spacerRef.current.style.height = '20px';
                return;
            }
            const bottomH = bottomBarRef.current ? bottomBarRef.current.offsetHeight : 0;
            const breathingRoom = 10;
            spacerRef.current.style.height = (bottomH + breathingRoom) + 'px';
        }
        function onResize(){
            computeSpacer();
            if(isMobileViewport()){
                const kbOpen = ivv.height < window.innerHeight - 80;
                if(kbOpen && inputRef.current){
                    setTimeout(()=>{ inputRef.current.scrollIntoView({behavior:'smooth',block:'nearest'}); }, 40);
                }
            }
        }
        computeSpacer();
        ivv.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', computeSpacer);
        window.addEventListener('load', computeSpacer);
        const ro = new ResizeObserver(()=>computeSpacer());
        if(bottomBarRef.current) ro.observe(bottomBarRef.current);
        if(suggestionRowRef.current) ro.observe(suggestionRowRef.current);
        return ()=>{
            ivv.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', computeSpacer);
            window.removeEventListener('load', computeSpacer);
            try { ro.disconnect(); } catch(e) {}
        };
    },[suggestions.length,isStreaming]);

    // Enhanced autoscroll threshold accounting for spacer height (Android had leftover gap)
    useEffect(()=>{
        const el = scrollRef.current; if(!el) return;
        if(!userScrolledUpRef.current){
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(()=>{ el.scrollTop = el.scrollHeight; });
        }
    }, [chatHistory]);

    // Streaming growth observer: ensure letter-by-letter growth stays pinned on Android too
    useEffect(()=>{
        if(!isStreaming) return; // only attach during streaming
        const el = scrollRef.current; if(!el) return;
        // Find last AI message container
        const aiBoxes = el.querySelectorAll('.conversation .aiBox');
        const lastAi = aiBoxes[aiBoxes.length-1];
        if(!lastAi) return;
        const ro = new ResizeObserver(()=>{
            if(!userScrolledUpRef.current){
                el.scrollTop = el.scrollHeight;
            }
        });
        ro.observe(lastAi);
        return ()=>{ try { ro.disconnect(); } catch(e){} };
    }, [isStreaming, chatHistory]);

    return (
        <div className="chatsection">
            <div className="topsection" ref={scrollRef}>
                {!showResult && chatHistory.length === 0 ? (
                    <div className="headings">
                        <span>HELLO {userName.toUpperCase()},</span>
                        <span>I'm VedaTron-AI...</span>
                        <span>Ask me anything — I'm here to help.</span> 
                    </div>
                ) : (
                    <div className="result conversation">
                        {chatHistory.map((msg, idx) => {
                            return (
                                <div key={idx} className={msg.role === 'user' ? 'userBox' : 'aiBox'}>
                                    <img src={msg.role === 'user' ? user : ai} alt="" width="50px" />
                                    <div style={{width:'100%'}}>
                                        {msg.role === 'model' ? (
                                            <MarkdownRender content={msg.content} />
                                        ) : (
                                            <p>{msg.content}</p>
                                        )}
                                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'6px',opacity:0.6,fontSize:'11px'}}>
                                            <span>{msg.time || ''}</span>
                                            {msg.role === 'model' ? (
                                                <button 
                                                    type="button"
                                                    onClick={() => navigator.clipboard.writeText(msg.content)}
                                                    style={{background:'none',border:'none',cursor:'pointer',color:'var(--color)',display:'flex',alignItems:'center',gap:'4px',fontSize:'11px',opacity:0.75,padding:0}}
                                                >
                                                    <LuCopy style={{width:'14px',height:'14px'}} />
                                                    <span>Copy</span>
                                                </button>
                                            ) : null}
                                        </div>
                    {/* Suggestions moved above input; only show regenerate beside last message if desired in future */}
                                    </div>
                                </div>
                            )
                        })}
                        {loading && !isStreaming && (
                            <div className="aiBox">
                                <img src={ai} alt="" width="50px" />
                                <div className="loader">
                                    <hr />
                                    <hr />
                                    <hr />
                                </div>
                            </div>
                        )}
                        { suggestions.length > 0 && !isStreaming && (
                            <div className="suggestion-row in-scroll" ref={suggestionRowRef}>
                                <button 
                                    onClick={regenerateLastAnswer}
                                    className="suggestion-btn regenerate"
                                >Regenerate</button>
                                { suggestions.map((s,i) => (
                                    <button 
                                        key={i}
                                        onClick={()=>applySuggestion(s)}
                                        className="suggestion-btn"
                                    >{s}</button>
                                ))}
                            </div>
                        ) }
                        {/* Spacer AFTER suggestions so user can scroll past entire suggestions row before bottom bar */}
                        <div className="conversation-spacer" aria-hidden="true" ref={spacerRef} />
                        <ResumePreview />
                    </div>
                )}
            </div>
            <div className="bottomsection" ref={bottomBarRef}>
                { (loading && !isStreaming) ? (
                    <div className="thinking-area">
                        <div className="dot" />
                        <div className="dot" />
                        <div className="dot" />
                        <span>VedaTron-AI is thinking...</span>
                    </div>
                ) : (
                    <>
                        <div className="composer-panel">
                            <div className="persona-template-row">
                                <div className="persona-bar">
                                    {visiblePersona.map(p => (
                                        <button key={p} onClick={()=>selectPersona(p)} className={`persona-btn ${persona===p?'active':''}`}>{p}</button>
                                    ))}
                                    {isMobile && (
                                        <button onClick={()=>setChipsExpanded(e=>!e)} className="persona-btn more-btn">{chipsExpanded? 'less' : 'more'}</button>
                                    )}
                                </div>
                                <div className="template-bar">
                                    {visibleTemplates.map(t => (
                                        <button key={t.key} onClick={()=>applyTemplate(t.key)} className="template-btn">{t.label}</button>
                                    ))}
                                </div>
                            </div>
                            { voiceMode ? (
                                <div className="voice-row">
                                    <div className={"listening-indicator" + (isListening? ' active':'') }>
                                        <div className="listening-ring ring1" />
                                        <div className="listening-ring ring2" />
                                        <div className="listening-core">{isListening? (voiceTranscript || 'Listening…') : (voiceTranscript ? 'Sending…' : 'Processing…')}</div>
                                    </div>
                                    <button className="cancel-voice-btn" onClick={()=>{ setVoiceMode(false); }} aria-label="Cancel voice input" title="Cancel voice input"><LuMicOff /></button>
                                    { recognitionError && <div className="voice-error">{recognitionError}</div> }
                                </div>
                            ) : (
                                <div className="input-row">
                                    <textarea
                                        ref={inputRef}
                                        onChange={(e)=> setInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Enter a prompt (Shift+Enter for newline)"
                                        value={input}
                                        rows={1}
                                        style={{resize:'none',whiteSpace:'pre-wrap',overflow:'hidden'}}
                                        onInput={(e)=>{
                                            // Fixed height behavior on mobile only
                                            if(window.matchMedia('(max-width:600px)').matches){
                                                e.target.style.height = '55px';
                                                return;
                                            }
                                            e.target.style.height = 'auto';
                                            const maxH = 180; // limit height (~8-9 lines)
                                            const newH = Math.min(e.target.scrollHeight, maxH);
                                            e.target.style.height = newH + 'px';
                                        }}
                                    />
                                    { !isStreaming && input ? (
                                        <button id="sentbtn" onClick={handleSend}>
                                            <LuSendHorizontal />
                                        </button>
                                    ) : null}
                                    { isStreaming && (
                                        <button id="sentbtn" onClick={stopStreaming} className="stop-btn" aria-label="Stop streaming" title="Stop streaming">
                                            <LuSquare />
                                        </button>
                                    ) }
                                    <button className="voice-toggle-btn" onClick={()=> setVoiceMode(true)} aria-label="Voice prompt" title="Voice prompt">
                                        <LuMic />
                                    </button>
                                    <div className="darkmode-wrapper">
                                        <Darkmode />
                                    </div>
                                </div>
                            ) }
                        </div>
                    </>
                )}
                { userScrolledUpRef.current && !isStreaming && (
                    <button onClick={jumpToLatest} style={{position:'absolute',right:'18px',top:'12px',background:'var(--btn-background-color)',color:'var(--btn-color)',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'20px',padding:'6px 12px',fontSize:'11px',cursor:'pointer',boxShadow:'0 2px 6px rgba(0,0,0,0.4)'}}>Jump to latest</button>
                ) }
            </div>
        </div>
    );
}

export default ChatSection;
